// Shared helpers used by Stage and fixture modules. Lives at module scope
// so fixture modules can import without pulling in the whole Stage class
// (which would create a circular import).

import * as THREE from "../../vendor/three.module.js";

export const DEG = Math.PI / 180;
export const LID_OPEN_ANGLE = Math.PI * 0.80;
export const COMIC_LEAN_BACK = 20;
export const COMIC_LEAN_FORWARD = 0;

export function easeInOutQuad(t) {
	return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

// Compute the per-comic storage-lane orientation. Slot positions specify the
// lane axis; we rotate so the comic's wide face points along the lane and the
// front cover faces toward +laneAxis.
export function getStorageLaneSlotQuaternion(slotOnePoint = null, slotTwoPoint = null) {
	const upAxis = new THREE.Vector3(0, 1, 0);
	const sequenceAxis = normalizeLaneVector(
		slotOnePoint && slotTwoPoint
			? new THREE.Vector3(
				slotTwoPoint.x - slotOnePoint.x,
				slotTwoPoint.y - slotOnePoint.y,
				slotTwoPoint.z - slotOnePoint.z,
			)
			: new THREE.Vector3(1, 0, 0),
	);
	if (Math.abs(sequenceAxis.x) >= Math.abs(sequenceAxis.z)) {
		if (sequenceAxis.x < 0) sequenceAxis.negate();
	} else {
		if (sequenceAxis.z < 0) sequenceAxis.negate();
	}
	const widthAxis = new THREE.Vector3().crossVectors(upAxis, sequenceAxis).normalize();
	const orientation = new THREE.Matrix4().makeBasis(widthAxis, upAxis, sequenceAxis);
	return new THREE.Quaternion().setFromRotationMatrix(orientation);
}

function normalizeLaneVector(vector) {
	const candidate = vector.clone();
	candidate.y = 0;
	if (candidate.lengthSq() < 1e-8) {
		return new THREE.Vector3(1, 0, 0);
	}
	return candidate.normalize();
}

// "Box forward" = direction from lane centre toward the extract point,
// projected onto the XZ plane. Used by hero camera + box-state UI.
export function boxForwardFromAnchors(heroExtract, laneCenter) {
	const fwd = new THREE.Vector3(
		heroExtract.x - laneCenter.x,
		0,
		heroExtract.z - laneCenter.z,
	);
	if (fwd.lengthSq() < 0.0001) fwd.set(0, 0, 1);
	return fwd.normalize();
}

// Outline overlay drawn behind a hovered mesh — duplicates the geometry with
// back-side material at slight upscale, attached as a child so it follows
// transforms without bookkeeping. Warm gold reads well over both dark
// longboxes and light comic covers; pure white was getting lost against
// bright artwork.
// 🔴 ASK-129 F-108c — OUTLINE WHATEVER IT IS HANDED, MESH OR GROUP.
//
// This read `mesh.geometry` and built one back-facing shell from it. A GROUP has no geometry, so
// `new THREE.Mesh(undefined, mat)` produced an outline that drew nothing — silently, with no error
// to notice. That matters because a seated product is only sometimes a single mesh: a catalogue
// product (`comic.glb`, `book.glb`) is a multi-mesh clone, and the thing worth registering as the
// interactable is its ROOT, so the hero pick-up lifts the whole item rather than one board of it.
// Outlining each child mesh is the same effect for a mesh (one child, itself) and the correct one
// for a group, so both callers get what they asked for and no call site has to know which it holds.
/** `tag` separates who lit it: the hover ("hover", every existing caller) and another shopper's point ("point", ASK-170
 *  §2 — the same material, so the glow reads as the hover's) each add and strip only their own shell. */
export function addOutline(mesh, tag = "hover") {
	if (!mesh) return;
	removeOutline(mesh, tag);
	const shell = (target) => {
		if (!target?.geometry) return;
		const outline = new THREE.Mesh(target.geometry, new THREE.MeshBasicMaterial({
			color:        0xffcc44,
			side:         THREE.BackSide,
			toneMapped:   false,   // saturated hint colour, not graded by scene exposure
		}));
		outline.scale.setScalar(1.06);
		outline.userData.isOutline = true;
		outline.userData.outlineTag = tag;
		// 🔴 A HIGHLIGHT IS RENDER-ONLY — it must never change what the crosshair hits. The shell is 6 %
		// bigger than the thing it lights, so as a raycast target it widened whatever was hovered:
		// measured (ASK-137 §91b) on Vanessa's pedestal, the lit magazine's shell caught the ray aimed
		// dead centre at the robot 0.23 m behind it, and E picked up the magazine. A lit item stayed
		// "hovered" by its own glow.
		outline.raycast = () => {};
		target.add(outline);
		// 🔴 ASK-159 §2.18gb ruling 5 — ANOTHER SHOPPER'S POINT READS THROUGH WHAT STANDS IN FRONT OF IT (the AAA ping
		// precedent: Apex / Fortnite pings draw through occluders). The rim above is depth-tested, so a pointed comic behind
		// a nearer one showed only the sliver of rim the nearer one did not cover (4-social P11 frame: the gold edge
		// around the pedestal's front comic was the aimed one's, behind it). This second shell draws ONLY where the item
		// is hidden (depthFunc GreaterDepth — the classic "x-ray" pass): the silhouette in translucent gold through the
		// occluder, nothing extra where the item is in plain view (its own nearer surface fails the test). The hover
		// keeps the rim only — a hover is under your own crosshair, never behind something.
		if (tag === "point") {
			const xray = new THREE.Mesh(target.geometry, new THREE.MeshBasicMaterial({
				color: 0xffcc44, transparent: true, opacity: 0.55, depthWrite: false,
				depthFunc: THREE.GreaterDepth, toneMapped: false,
				// pulled a hair TOWARD the camera: on the item's own visible surface the shell is then nearer than the depth the
				// item wrote and the Greater test fails cleanly (coplanar depth would speckle), while behind an occluder it is
				// still farther than the occluder and draws
				polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -4,
			}));
			xray.renderOrder = 10;   // after the opaque scene, so the occluder's depth is already written
			xray.userData.isOutline = true;
			xray.userData.outlineTag = tag;
			xray.userData.seenThrough = true;
			xray.raycast = () => {};
			target.add(xray);
		}
	};
	if (mesh.geometry) { shell(mesh); return; }
	mesh.traverse((o) => { if (o.isMesh && !o.userData.isOutline) shell(o); });
}

export function removeOutline(mesh, tag = "hover") {
	if (!mesh) return;
	// Mirrors `addOutline`: a group's outline lives on its child meshes, so clearing only the
	// direct children would strand one shell per board and the item would stay lit for good.
	const strip = (target) => {
		// every shell of this tag (a point lights two: the rim and the seen-through pass)
		for (const child of (target.children || []).filter((c) => c.userData?.isOutline && (c.userData.outlineTag || "hover") === tag)) {
			child.material.dispose();
			target.remove(child);
		}
	};
	strip(mesh);
	if (!mesh.geometry) mesh.traverse((o) => { if (o.isMesh) strip(o); });
}
